import React, { useEffect, useMemo, useState } from 'react';
import type { ChecklistItemTemplate, ChecklistItemAnswerDraft, ItemStatus } from '../../../modules/checklist/types/checklistItems';
import ChecklistItemRow from './ChecklistItemRow';
import ConfirmModal from '../confirm/ConfirmModal';

type ChecklistItemsTabProps = {
  templates: ChecklistItemTemplate[];
  answers: Record<string, ChecklistItemAnswerDraft>;
  disabled?: boolean;
  readOnly?: boolean;
  onStatusChange: (key: string, status: ItemStatus) => void;
  onNoteChange: (key: string, note: string) => void;
  onMarkAllOk: () => void;
  onClear: () => void;
  pendingByCategory: Record<string, number>;
};

type CategoryGroup = {
  key: string;
  label: string;
  templates: ChecklistItemTemplate[];
};

const ChecklistItemsTab: React.FC<ChecklistItemsTabProps> = ({
  templates,
  answers,
  disabled,
  readOnly,
  onStatusChange,
  onNoteChange,
  onMarkAllOk,
  onClear,
  pendingByCategory,
}) => {
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});

  const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; message: string; confirmLabel?: string; onConfirm: () => void }>({
    isOpen: false,
    message: '',
    onConfirm: () => {},
  });

  const openConfirm = (message: string, onConfirm: () => void, confirmLabel?: string) => {
    setConfirmModal({ isOpen: true, message, confirmLabel, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Agrupa por categoria dinâmica preservando a ordem em que os templates chegam
  const groups = useMemo<CategoryGroup[]>(() => {
    const byCategory = new Map<string, CategoryGroup>();

    for (const template of templates) {
      const key = template.category || 'SEM_CATEGORIA';
      if (!byCategory.has(key)) {
        byCategory.set(key, {
          key,
          label: template.categoryLabel || key,
          templates: [],
        });
      }
      byCategory.get(key)!.templates.push(template);
    }

    const list = Array.from(byCategory.values());
    for (const group of list) {
      group.templates.sort((a, b) => a.order - b.order);
    }
    return list;
  }, [templates]);

  // Abre a primeira categoria por padrão
  useEffect(() => {
    if (groups.length === 0) return;
    setOpenCategories((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      return { [groups[0].key]: true };
    });
  }, [groups]);

  const total = templates.length;
  const answered = templates.filter((t) => Boolean(answers[t.key]?.status)).length;

  return (
    <>
    <ConfirmModal
      isOpen={confirmModal.isOpen}
      message={confirmModal.message}
      confirmLabel={confirmModal.confirmLabel}
      onConfirm={confirmModal.onConfirm}
      onCancel={closeConfirm}
    />
    <div className="checklist-section">
      <div className="checklist-section-title">Itens</div>
      <div className="checklist-section-desc">Confira todos os itens do veículo e selecione um status para cada linha.</div>

      <div className="items-summary">
        <div><strong>Preenchidos:</strong> {answered}/{total}</div>
      </div>

      {!readOnly && (
        <div className="items-quick-actions">
          <button
            type="button"
            className="checklist-btn secondary"
            disabled={disabled}
            onClick={() => openConfirm('Marcar todos os itens como OK?', () => { closeConfirm(); onMarkAllOk(); }, 'Marcar')}
          >
            Marcar tudo OK
          </button>
          <button
            type="button"
            className="checklist-btn secondary"
            disabled={disabled}
            onClick={() => openConfirm('Limpar todas as respostas dos itens?', () => { closeConfirm(); onClear(); })}
          >
            Limpar respostas
          </button>
        </div>
      )}

      <div className="items-accordion-list">
        {groups.map((group) => {
          const isOpen = Boolean(openCategories[group.key]);
          const pending = pendingByCategory[group.key] || 0;

          return (
            <div key={group.key} className="items-accordion-card">
              <button
                type="button"
                className={isOpen ? 'items-accordion-head open' : 'items-accordion-head'}
                onClick={() => setOpenCategories((prev) => ({ ...prev, [group.key]: !prev[group.key] }))}
              >
                <span className="items-accordion-title">{group.label}</span>
                <span className="items-accordion-meta">
                  {pending > 0 && <span className="items-pending-badge">Pendências: {pending}</span>}
                  <span className="items-accordion-count">{group.templates.length} itens</span>
                </span>
              </button>

              {isOpen && (
                <div className="items-accordion-body">
                  <div className="items-table-head">
                    <span>Item</span>
                    <span>Status</span>
                    <span>Observação</span>
                  </div>
                  {group.templates.map((template) => (
                    <ChecklistItemRow
                      key={template.key}
                      template={template}
                      answer={answers[template.key] || { status: null, note: '' }}
                      disabled={disabled}
                      readOnly={readOnly}
                      onStatusChange={onStatusChange}
                      onNoteChange={onNoteChange}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
    </>
  );
};

export default ChecklistItemsTab;
